import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarBlank, DownloadSimple, XCircle, FilePdf, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { PageLoader, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(false);

  const load = () => api.get(`/bookings/${id}`).then((r) => setB(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const cancel = async () => {
    try { const { data } = await api.post(`/bookings/${id}/cancel`); toast.success(data.message); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPdf = async () => {
    setDl(true);
    try {
      const res = await api.get(`/bookings/${id}/ticket.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EventVexa-Ticket-${(b.event_title || "event").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Ticket PDF downloaded.");
    } catch (e) { toast.error("Couldn't download the ticket PDF."); }
    finally { setDl(false); }
  };

  const sendWhatsapp = () => {
    const first = (b.tickets || [])[0];
    const link = first ? `${window.location.origin}/t/${first.share_token}` : `${window.location.origin}/bookings/${id}`;
    const url = b.whatsapp_link || `https://wa.me/?text=${encodeURIComponent(`My EventVexa ticket for ${b.event_title} (${b.occurrence_label || ""}). View: ${link}`)}`;
    window.open(url, "_blank");
  };

  if (loading) return <PageLoader />;
  if (!b) return <div className="p-20 text-center text-muted-foreground">Booking not found.</div>;

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-6 py-8">
      <button onClick={() => navigate("/bookings")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5"><ArrowLeft size={16} /> My Bookings</button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{b.event_title}</h1>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><CalendarBlank size={15} /> {b.show_date} · {b.show_time}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {b.venue}, {b.city}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {b.event_status_label && <StatusBadge status={b.event_lifecycle_status === "completed" ? "completed" : "cancelled"} />}
          <StatusBadge status={b.status} />
        </div>
      </div>

      {b.event_status_label && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="booking-event-status">
          {b.event_status_label}. Your booking history and payment details remain available below.
        </div>
      )}

      {b.status === "confirmed" && !b.event_status_label && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <EnvelopeSimple size={17} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>A branded <span className="font-semibold text-foreground">PDF ticket</span> has been emailed to you. You can also download it or share it on WhatsApp below.</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={downloadPdf} disabled={dl} className="rounded-full" data-testid="download-pdf-btn"><FilePdf className="mr-1.5" /> {dl ? "Preparing…" : "Download PDF ticket"}</Button>
            <Button onClick={sendWhatsapp} variant="outline" className="rounded-full border-emerald-500/40 text-emerald-700" data-testid="send-whatsapp-btn"><WhatsappLogo className="mr-1.5" /> Send on WhatsApp</Button>
            {(b.tickets || []).some((t) => t.map_link) && (
              <Button onClick={() => window.open((b.tickets.find((t) => t.map_link) || {}).map_link, "_blank")} variant="outline" className="rounded-full" data-testid="directions-btn"><MapPin className="mr-1.5" /> Get directions</Button>
            )}
          </div>
        </div>
      )}

      {/* tickets */}
      <div className="space-y-4">
        {(b.tickets || []).map((t) => (
          <div key={t.id} data-testid={`ticket-${t.id}`} className="flex flex-col sm:flex-row items-center gap-5 rounded-xl border border-border bg-card p-5">
            <div className="bg-white p-2 rounded-lg border border-border">
              <img src={t.qr} alt="QR" className="w-32 h-32" data-testid={`qr-${t.id}`} />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="overline">{t.category}</div>
              <div className="font-heading text-xl font-semibold tracking-tight mt-1">{t.event_title}</div>
              <div className="text-sm text-muted-foreground mt-1">Ticket code: <span className="font-mono">{t.code}</span></div>
              <div className="mt-2"><StatusBadge status={t.checked_in ? "verified" : t.status} /> {t.checked_in && <span className="text-xs text-muted-foreground ml-1">Checked in</span>}</div>
            </div>
          </div>
        ))}
      </div>

      {/* invoice */}
      {b.invoice && (
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Invoice</div>
              <div className="font-mono text-sm mt-0.5">{b.invoice.invoice_number}</div>
            </div>
            <Button variant="outline" onClick={() => window.print()} className="rounded-full"><DownloadSimple className="mr-1.5" /> Print / Save</Button>
          </div>
          <div className="text-sm space-y-1.5">
            {(b.line_items || []).map((li, i) => (
              <div key={i} className="flex justify-between"><span className="text-muted-foreground">{li.name} × {li.qty}</span><span>{money(li.line_total)}</span></div>
            ))}
            {b.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span>− {money(b.discount)}</span></div>}
            {b.convenience_fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Convenience fee</span><span>{money(b.convenience_fee)}</span></div>}
            {b.gst > 0 && <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span>{money(b.gst)}</span></div>}
            {b.credits_amount > 0 && <div className="flex justify-between text-emerald-700"><span>Event credits</span><span>− {money(b.credits_amount)}</span></div>}
            <div className="h-px bg-border my-2" />
            <div className="flex justify-between font-semibold text-base"><span>Total paid</span><span>{money(b.total)}</span></div>
          </div>
        </div>
      )}

      {b.status === "confirmed" && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="mt-6 rounded-full text-destructive border-destructive/30" data-testid="cancel-booking-btn"><XCircle className="mr-1.5" /> Cancel Booking</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
              <AlertDialogDescription>Your tickets will be invalidated and a refund will be initiated per the event's policy. Earned credits will be reversed.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep booking</AlertDialogCancel>
              <AlertDialogAction onClick={cancel} data-testid="confirm-cancel">Yes, cancel</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
